import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { generateChordVoicing } from '@/music/voicing';
import { APP_ROUTES } from '@/navigation';
import '@/learn.css';

const C_MAJOR_SEVEN = generateChordVoicing('C', 'maj7', 0, 'middle', 'drop-2');

const TOP_VOICES = [
  {
    topVoice: '7th · B',
    notes: 'G · C · E · B',
    bass: '5th · G',
    inversion: '2nd inversion',
  },
  {
    topVoice: 'Root · C',
    notes: 'B · E · G · C',
    bass: '7th · B',
    inversion: '3rd inversion',
  },
  {
    topVoice: '3rd · E',
    notes: 'C · G · B · E',
    bass: 'Root · C',
    inversion: 'Root position',
  },
  {
    topVoice: '5th · G',
    notes: 'E · B · C · G',
    bass: '3rd · E',
    inversion: '1st inversion',
  },
] as const;

export function Drop2IntroPage() {
  return (
    <article className="drop2-page">
      <nav className="learn-breadcrumb" aria-label="Breadcrumb">
        <Link to={APP_ROUTES.learn}>Learn</Link>
        <span aria-hidden="true">/</span>
        <Link to={APP_ROUTES.fourNoteVoicings}>Four-note voicings</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Drop 2</span>
      </nav>

      <header className="drop2-hero">
        <p className="learn-eyebrow">Voicing family</p>
        <h1>Drop 2</h1>
        <p>
          A Drop 2 voicing starts as a four-note close-position chord. Its second-highest voice
          moves down one octave, which spreads the notes across four adjacent strings.
        </p>
      </header>

      <nav className="drop2-toc" aria-label="On this page">
        <a href="#build">Building</a>
        <a href="#top-voices">Top voices and inversions</a>
        <a href="#string-sets">String layouts</a>
        <a href="#voice-leading">Voice leading</a>
        <a href="#avoid-b9">The b9 rule</a>
      </nav>

      <section id="build" className="drop2-section" aria-labelledby="drop2-build-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">01</span>
          <div>
            <h2 id="drop2-build-heading">Building a Drop 2 voicing</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Close-position Cmaj7, low to high, is C, E, G, B. The second voice from the top is G.
          Lower G by one octave.
        </p>

        <figure className="drop2-transformation">
          <div className="drop2-stack-wrap">
            <p className="drop2-stack-title">Close position</p>
            <div className="drop2-stack" aria-label="Close position, low to high: C, E, G, B">
              <span className="drop2-register">High</span>
              <div className="drop2-voice">
                <strong>B</strong>
                <span>7</span>
              </div>
              <div className="drop2-voice drop2-voice--picked">
                <strong>G</strong>
                <span>5 · second-highest</span>
              </div>
              <div className="drop2-voice">
                <strong>E</strong>
                <span>3</span>
              </div>
              <div className="drop2-voice drop2-voice--root">
                <strong>C</strong>
                <span>1 · root</span>
              </div>
              <span className="drop2-register">Low</span>
            </div>
          </div>

          <div className="drop2-operation" aria-label="Move G down one octave">
            <span>Drop voice 2</span>
            <ArrowRight size={24} aria-hidden="true" />
            <small>G moves down one octave</small>
          </div>

          <div className="drop2-stack-wrap">
            <p className="drop2-stack-title">Drop 2</p>
            <div className="drop2-stack" aria-label="Drop 2 voicing, low to high: G, C, E, B">
              <span className="drop2-register">High</span>
              <div className="drop2-voice">
                <strong>B</strong>
                <span>7</span>
              </div>
              <div className="drop2-voice">
                <strong>E</strong>
                <span>3</span>
              </div>
              <div className="drop2-voice drop2-voice--root">
                <strong>C</strong>
                <span>1 · root</span>
              </div>
              <div className="drop2-voice drop2-voice--dropped">
                <strong>G</strong>
                <span>5 · dropped</span>
              </div>
              <span className="drop2-register">Low</span>
            </div>
          </div>

          <figcaption>
            Low to high: <strong>C–E–G–B</strong> becomes <strong>G–C–E–B</strong>. No chord tone is
            added or removed. Only G changes octave.
          </figcaption>
        </figure>

        <div className="drop2-result">
          <div>
            <h3>Cmaj7, lower layout</h3>
            <p>
              The voicing fits on four adjacent strings. The interval labels read low to high: 5,
              root, 3, 7.
            </p>
            <p className="drop2-hint">
              Press the diagram to play it. Double-click or double-tap it to see the notes and
              frets.
            </p>
          </div>
          {C_MAJOR_SEVEN && (
            <PlayableDiagram
              className="drop2-example-diagram"
              chord={{
                fingering: C_MAJOR_SEVEN,
                rootDisplay: 'C',
                chordType: 'maj7',
                symbol: 'Cmaj7',
                inversion: 0,
                stringSet: 'middle',
              }}
              caption="2nd inversion · 7th on top"
            />
          )}
        </div>
      </section>

      <section id="top-voices" className="drop2-section" aria-labelledby="drop2-top-voices-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">02</span>
          <div>
            <h2 id="drop2-top-voices-heading">Top voices and inversions</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Rotate the close-position chord first, then drop voice 2. The rotation sets the top voice.
          The bass note after the drop sets the inversion.
        </p>

        <div className="drop2-table-wrap">
          <table className="drop2-inversion-table">
            <thead>
              <tr>
                <th scope="col">Top voice</th>
                <th scope="col">Drop 2, low → high</th>
                <th scope="col">Finished bass</th>
                <th scope="col">Finished inversion</th>
              </tr>
            </thead>
            <tbody>
              {TOP_VOICES.map((voicing) => (
                <tr key={voicing.topVoice}>
                  <th scope="row">{voicing.topVoice}</th>
                  <td className="drop2-note-sequence">{voicing.notes}</td>
                  <td>{voicing.bass}</td>
                  <td>{voicing.inversion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="drop2-principle">
          <strong>Naming</strong>
          <p>
            Choose the top voice for the melody and the position on the neck. Name a voicing by its
            inversion and its top note, for example “2nd inversion · 7th on top”.
          </p>
        </aside>
      </section>

      <section id="string-sets" className="drop2-section" aria-labelledby="drop2-strings-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">03</span>
          <div>
            <h2 id="drop2-strings-heading">String layouts</h2>
          </div>
        </div>
        <p className="drop2-lede">
          The workbench uses two groups of four adjacent strings. A shape on the other layout is
          sometimes the closest move.
        </p>
        <div className="drop2-string-grid">
          <article>
            <p className="learn-eyebrow">Lower layout</p>
            <p>A, D, G and B strings. Used for comping. The top voice is on the B string.</p>
            <div className="drop2-string-map" aria-label="Middle string set uses A, D, G and B">
              <span>E</span>
              <strong>A</strong>
              <strong>D</strong>
              <strong>G</strong>
              <strong>B</strong>
              <span>E</span>
            </div>
          </article>
          <article>
            <p className="learn-eyebrow">Upper layout</p>
            <p>
              D, G, B and high E strings. Used for chord melody. The top voice is on the high E
              string.
            </p>
            <div className="drop2-string-map" aria-label="Upper string set uses D, G, B and high E">
              <span>E</span>
              <span>A</span>
              <strong>D</strong>
              <strong>G</strong>
              <strong>B</strong>
              <strong>E</strong>
            </div>
          </article>
        </div>
      </section>

      <section id="voice-leading" className="drop2-section" aria-labelledby="drop2-leading-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">04</span>
          <div>
            <h2 id="drop2-leading-heading">Voice leading</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Keep common tones in the same voice and move the other voices by the smallest interval.
        </p>

        <div
          className="drop2-guide-line"
          aria-label="Example top line: F over D minor 7, F over G7, E over C major 7"
        >
          <div>
            <span>Dm7</span>
            <strong>F</strong>
            <small>start</small>
          </div>
          <ArrowRight size={19} aria-hidden="true" />
          <div>
            <span>G7</span>
            <strong>F</strong>
            <small>hold</small>
          </div>
          <ArrowRight size={19} aria-hidden="true" />
          <div>
            <span>Cmaj7</span>
            <strong>E</strong>
            <small>down ½ step</small>
          </div>
        </div>

        <div className="drop2-two-column">
          <div>
            <h3>Check for</h3>
            <ul>
              <li>Common tones held in the same voice.</li>
              <li>Guide tones resolving by a half or whole step.</li>
              <li>A top line that works as a melody on its own.</li>
            </ul>
          </div>
          <div>
            <h3>The workbench</h3>
            <p>
              The optimiser compares fingerings, minimises fret movement and puts a requested lead
              note on the top string. Adjust its result by ear.
            </p>
          </div>
        </div>
      </section>

      <section id="avoid-b9" className="drop2-section" aria-labelledby="drop2-b9-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">05</span>
          <div>
            <h2 id="drop2-b9-heading">The b9 rule</h2>
          </div>
        </div>
        <div className="drop2-b9-callout">
          <div
            className="drop2-b9-interval"
            aria-label="An octave plus one semitone equals a minor ninth"
          >
            <strong>8ve</strong>
            <span>+</span>
            <strong>½ step</strong>
            <span>=</span>
            <strong>♭9</strong>
          </div>
          <div>
            <p>
              Two voices a minor ninth apart sound harsh. The <strong>Avoid b9</strong> setting
              removes these shapes from the optimiser's search.
            </p>
            <p>
              Chords that contain a ♭9, such as 7♭9, are exempt. In ninth and extension voicings the
              extension replaces the root, so the chord still has four voices.
            </p>
          </div>
        </div>
      </section>

      <footer className="drop2-next">
        <h2>Next</h2>
        <div className="drop2-next-grid">
          <Link to={`${APP_ROUTES.chordLibrary}?family=drop-2`}>
            <span>Reference</span>
            <strong>Chord library</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={APP_ROUTES.progressions}>
            <span>Practice</span>
            <strong>Loop a ii–V–I</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={APP_ROUTES.studies}>
            <span>Course</span>
            <strong>Drop 2 studies</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={`${APP_ROUTES.workbench}?family=drop-2`}>
            <span>Apply</span>
            <strong>Open the Workbench</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </div>
      </footer>
    </article>
  );
}
